import React from 'react';
import {StyleSheet, View} from 'react-native';

import {
  CAROUSEL_ASPECT_RATIO,
  CAROUSEL_BORDER_RADIUS,
  CAROUSEL_DOTS,
} from '../../HomepageCarousel/constants';
import {STAGGER} from '../constants';
import {Bone} from '../primitives/Bone';
import {BoneIcon} from '../primitives/BoneIcon';

type BannerSkeletonProps = {
  aspectRatio?: number;
  borderRadius?: number;
  showDots?: boolean;
  dotCount?: number;
  maxHeight?: number;
};

/**
 * Skeleton for banner/hero carousel images.
 * Uses a plain Bone inside an aspect-ratio wrapper to support dynamic sizing
 * (BoneImage banner variant is fixed at 160px height).
 * Dot row uses static positioning to match HomepageCarousel's layout height.
 */
export const BannerSkeleton: React.FC<BannerSkeletonProps> = React.memo(
  ({
    aspectRatio = CAROUSEL_ASPECT_RATIO,
    borderRadius = CAROUSEL_BORDER_RADIUS,
    showDots = false,
    dotCount = 3,
    maxHeight,
  }) => (
    <View style={[styles.container, maxHeight != null && {maxHeight, overflow: 'hidden'}]}>
      <View
        style={[
          styles.imageWrapper,
          {aspectRatio, borderRadius},
          maxHeight != null && {maxHeight},
        ]}>
        <Bone
          width="100%"
          height={9999}
          borderRadius={borderRadius}
          animationDelay={STAGGER.delay(0)}
        />
      </View>
      {showDots ? (
        <View style={styles.dotRow}>
          {Array.from({length: dotCount}, (_, i) => (
            <BoneIcon key={i} size={CAROUSEL_DOTS.dotSize} animationDelay={STAGGER.delay(i + 1)} />
          ))}
        </View>
      ) : null}
    </View>
  ),
);

BannerSkeleton.displayName = 'BannerSkeleton';

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  imageWrapper: {
    overflow: 'hidden',
  },
  dotRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
    marginTop: CAROUSEL_DOTS.marginTop,
    marginBottom: CAROUSEL_DOTS.marginBottom,
  },
});
